import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Switch,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Platform,
  FlatList,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { useThemedStyles } from '../hooks/useThemedStyles';

// ─── Confirm dialog modal ─────────────────────────────────────────────────────
const ConfirmModal: React.FC<{
  visible: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ visible, title, message, onConfirm, onCancel }) => {
  const cm = useThemedStyles(getCm);
  if (!visible) return null;
  const content = (
    <View style={cm.box}>
      <Text style={cm.title}>{title}</Text>
      <Text style={cm.message}>{message}</Text>
      <View style={cm.btnRow}>
        <TouchableOpacity style={cm.cancelBtn} onPress={onCancel} activeOpacity={0.7}>
          <Text style={cm.cancelText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={cm.deleteBtn} onPress={onConfirm} activeOpacity={0.7}>
          <Text style={cm.deleteText}>Remove</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
  if (Platform.OS === 'web') {
    return (
      <View style={cm.overlay}>
        <TouchableOpacity style={cm.backdrop} activeOpacity={1} onPress={onCancel} />
        {content}
      </View>
    );
  }
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <View style={cm.overlay}>
        <TouchableOpacity style={cm.backdrop} activeOpacity={1} onPress={onCancel} />
        {content}
      </View>
    </Modal>
  );
};
const getCm = () => StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFillObject, zIndex: 100, justifyContent: 'center', alignItems: 'center' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.45)' },
  box: { width: 300, backgroundColor: colors.background.surface, borderRadius: 14, padding: 24, gap: 12, elevation: 10, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 12, shadowOffset: { width: 0, height: 4 } },
  title: { fontSize: 16, fontWeight: '700', color: '#111', textAlign: 'center' },
  message: { fontSize: 14, color: '#555', textAlign: 'center', lineHeight: 20 },
  btnRow: { flexDirection: 'row', gap: 10, marginTop: 4 },
  cancelBtn: { flex: 1, borderRadius: 8, borderWidth: 1, borderColor: '#ddd', paddingVertical: 10, alignItems: 'center' },
  cancelText: { fontSize: 14, color: '#555', fontWeight: '500' },
  deleteBtn: { flex: 1, borderRadius: 8, backgroundColor: '#E53E3E', paddingVertical: 10, alignItems: 'center' },
  deleteText: { fontSize: 14, color: '#fff', fontWeight: '600' },
});
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, FormHeader, ContactSelector } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { apiService } from '../services/api';
import { ZoneMapPicker, ZoneResult } from '../components/ZoneMapPicker';

type Tab = 'tax' | 'charges' | 'discount';
const PRODUCT_TYPES = ['physical', 'digital', 'service'] as const;
type ProductType = typeof PRODUCT_TYPES[number];

interface NamedItem { id: string; name: string; }

// ─── Shared multi-select picker (catalogues) ──────────────────────────────────

const MultiPicker: React.FC<{
  visible: boolean; title: string;
  selected: NamedItem[]; items: NamedItem[];
  loading: boolean; search: string;
  onSearchChange: (q: string) => void;
  onClose: () => void; onConfirm: (items: NamedItem[]) => void;
}> = ({ visible, title, selected, items, loading, search, onSearchChange, onClose, onConfirm }) => {
  const mp = useThemedStyles(getMp);
  const s = useThemedStyles(getS);
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (visible) {
      const m = new Map<string, string>();
      selected.forEach(s => m.set(s.id, s.name));
      setPicked(m);
    }
  }, [visible]);
  const toggle = (item: NamedItem) => setPicked(prev => {
    const next = new Map(prev);
    if (next.has(item.id)) next.delete(item.id); else next.set(item.id, item.name);
    return next;
  });
  const confirm = () => { onConfirm(Array.from(picked.entries()).map(([id, name]) => ({ id, name }))); onClose(); };
  const content = (
    <View style={{ flex: 1, backgroundColor: colors.background.surface }}>
      <View style={mp.header}>
        <Text style={mp.title}>{title}</Text>
        <TouchableOpacity onPress={onClose} activeOpacity={0.7}><Ionicons name="close" size={24} color={colors.textLight} /></TouchableOpacity>
      </View>
      <View style={mp.searchBox}>
        <Ionicons name="search" size={18} color={colors.textLight} />
        <TextInput style={mp.searchInput} value={search} onChangeText={onSearchChange} placeholder={`Search…`} placeholderTextColor={colors.textLight} />
        {search.length > 0 && <TouchableOpacity onPress={() => onSearchChange('')} activeOpacity={0.7}><Ionicons name="close-circle" size={18} color={colors.textLight} /></TouchableOpacity>}
      </View>
      {loading ? <View style={mp.center}><ActivityIndicator color={colors.primary} /></View> : (
        <FlatList data={items} keyExtractor={i => i.id} renderItem={({ item }) => {
          const sel = picked.has(item.id);
          return (
            <TouchableOpacity style={mp.row} onPress={() => toggle(item)} activeOpacity={0.7}>
              <View style={[mp.checkbox, sel && mp.checkboxChecked]}>{sel && <Ionicons name="checkmark" size={14} color={colors.white} />}</View>
              <Text style={mp.itemName}>{item.name}</Text>
            </TouchableOpacity>
          );
        }} ListEmptyComponent={<View style={mp.center}><Text style={{ color: colors.textLight }}>No results</Text></View>} />
      )}
      <View style={mp.footer}>
        <Text style={{ color: colors.textSecondary, fontSize: typography.fontSize.sm }}>{picked.size} selected</Text>
        <TouchableOpacity style={mp.confirmBtn} onPress={confirm} activeOpacity={0.8}><Text style={mp.confirmText}>Confirm</Text></TouchableOpacity>
      </View>
    </View>
  );
  if (Platform.OS === 'web') {
    if (!visible) return null;
    return (
      <View style={mp.webOverlay}>
        <TouchableOpacity style={mp.webBackdrop} activeOpacity={1} onPress={onClose} />
        <View style={mp.webSheet}>{content}</View>
      </View>
    );
  }
  return <Modal visible={visible} animationType="slide" presentationStyle="formSheet">{content}</Modal>;
};
const getMp = () => StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  searchBox: { flexDirection: 'row', alignItems: 'center', margin: spacing.md, borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, gap: spacing.xs },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.base, color: colors.textDark },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, gap: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border + '50' },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  itemName: { fontSize: typography.fontSize.base, color: colors.textDark },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderTopWidth: 1, borderTopColor: colors.border },
  confirmBtn: { backgroundColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  confirmText: { color: colors.white, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  webOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 10 },
  webBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  webSheet: { position: 'absolute' as any, top: spacing.xl * 2, left: spacing.xl * 3, right: spacing.xl * 3, bottom: spacing.xl * 2, backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, overflow: 'hidden', ...designTokens.shadows.lg },
});

// ─── Tag pills ────────────────────────────────────────────────────────────────
const TagRow: React.FC<{ items: NamedItem[]; onRemove: (id: string) => void }> = ({ items, onRemove }) => {
  const tag = useThemedStyles(getTag);
  if (!items.length) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs }}>
      {items.map(item => (
        <View key={item.id} style={tag.pill}>
          <Text style={tag.label}>{item.name}</Text>
          <TouchableOpacity onPress={() => onRemove(item.id)} activeOpacity={0.7} style={{ marginLeft: 4 }}>
            <Ionicons name="close-circle" size={14} color={colors.primary} />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};
const getTag = () => StyleSheet.create({
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary + '15', borderRadius: 20, paddingHorizontal: spacing.sm, paddingVertical: 3, borderWidth: 1, borderColor: colors.primary + '40' },
  label: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});

// ─── Targeting sub-form (contacts + products + catalogues) ───────────────────
interface TargetingState {
  contacts: NamedItem[];
  products: NamedItem[];
  catalogues: NamedItem[];
}
const emptyTargeting = (): TargetingState => ({ contacts: [], products: [], catalogues: [] });

const TargetingSection: React.FC<{
  value: TargetingState;
  onChange: (v: TargetingState) => void;
  label?: string;
}> = ({ value, onChange, label }) => {
  const s = useThemedStyles(getS);
  const [pickerContact, setPickerContact] = useState<any>(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showCataloguePicker, setShowCataloguePicker] = useState(false);
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [catalogueItems, setCatalogueItems] = useState<NamedItem[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const catTimer = useRef<any>(null);

  const loadCatalogues = async (q: string) => {
    setCatalogueLoading(true);
    try {
      const res = await apiService.getCatalogues({ search: q || undefined, limit: 50 });
      if (res.statusCode === 1000) {
        setCatalogueItems((res.data?.catalogues ?? []).map((c: any) => ({ id: c._id, name: c.name })));
      }
    } catch (_) {} finally { setCatalogueLoading(false); }
  };

  const addContact = (contact: any) => {
    const id = contact._id ?? contact.id ?? '';
    const name = contact.name ?? contact.fullName ?? id;
    if (!id || value.contacts.some(c => c.id === id)) return;
    onChange({ ...value, contacts: [...value.contacts, { id, name }] });
    setPickerContact(null);
  };

  return (
    <View style={{ gap: spacing.sm }}>
      {label && <Text style={s.sectionSubLabel}>{label}</Text>}

      {/* Contacts */}
      <View style={s.field}>
        <Text style={s.fieldLabel}>Specific Contacts (empty = all)</Text>
        <TagRow items={value.contacts} onRemove={id => onChange({ ...value, contacts: value.contacts.filter(c => c.id !== id) })} />
        <Text style={[s.hint, { marginTop: spacing.xs }]}>Search and select to add:</Text>
        <ContactSelector mode="edit" variant="simple" contact={pickerContact} onContactSelect={addContact} />
      </View>

      {/* Products */}
      <View style={s.field}>
        <Text style={s.fieldLabel}>Specific Products (empty = all)</Text>
        <TagRow items={value.products} onRemove={id => onChange({ ...value, products: value.products.filter(p => p.id !== id) })} />
        <TouchableOpacity style={s.pickerBtn} onPress={() => setShowProductPicker(true)} activeOpacity={0.7}>
          <Ionicons name="cube-outline" size={16} color={colors.primary} />
          <Text style={s.pickerBtnText}>{value.products.length ? 'Edit Products' : 'Add Products'}</Text>
        </TouchableOpacity>
      </View>

      {/* Catalogues */}
      <View style={s.field}>
        <Text style={s.fieldLabel}>Catalogues (empty = all)</Text>
        <TagRow items={value.catalogues} onRemove={id => onChange({ ...value, catalogues: value.catalogues.filter(c => c.id !== id) })} />
        <TouchableOpacity style={s.pickerBtn} onPress={() => { setCatalogueSearch(''); loadCatalogues(''); setShowCataloguePicker(true); }} activeOpacity={0.7}>
          <Ionicons name="albums-outline" size={16} color={colors.primary} />
          <Text style={s.pickerBtnText}>{value.catalogues.length ? 'Edit Catalogues' : 'Add Catalogues'}</Text>
        </TouchableOpacity>
      </View>

      <ProductSelectorModal
        visible={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        title="Select Products"
        confirmText="Add Selected"
        allowMultiple
        onConfirm={async (selectedIds) => {
          setShowProductPicker(false);
          const existing = new Map(value.products.map(p => [p.id, p.name]));
          const newIds = selectedIds.filter(id => !existing.has(id));
          const resolved = await Promise.all(newIds.map(async id => {
            try {
              const res = await apiService.getProductById(id);
              return { id, name: res.statusCode === 1000 ? (res.data?.name ?? id) : id };
            } catch { return { id, name: id }; }
          }));
          const selectedSet = new Set(selectedIds);
          const kept = value.products.filter(p => selectedSet.has(p.id));
          onChange({ ...value, products: [...kept, ...resolved] });
        }}
      />

      <MultiPicker
        visible={showCataloguePicker}
        title="Select Catalogues"
        selected={value.catalogues}
        items={catalogueItems}
        loading={catalogueLoading}
        search={catalogueSearch}
        onSearchChange={q => { setCatalogueSearch(q); clearTimeout(catTimer.current); catTimer.current = setTimeout(() => loadCatalogues(q), 350); }}
        onClose={() => setShowCataloguePicker(false)}
        onConfirm={items => onChange({ ...value, catalogues: items })}
      />
    </View>
  );
};

// ─── Config zone (mirrors API ConfigZone) ─────────────────────────────────────

interface ConfigZone {
  name: string;
  mode?: 'country' | 'city' | 'radius';
  cities?: string[];
  polygon?: [number, number][];
  polygons?: [number, number][][];
  center?: { latitude: number; longitude: number };
  radiusKm?: number;
  value?: number;            // override rate/amount
  type?: 'percentage' | 'flat';
  enabled?: boolean;
}

// ─── Form interfaces ──────────────────────────────────────────────────────────

interface TaxForm {
  id: string; enabled: boolean; rate: string; name: string;
  inclusive: boolean; appliesTo: ProductType[];
  zones: ConfigZone[];
}

interface ChargeForm {
  name: string; type: 'percentage' | 'flat'; value: string; enabled: boolean;
  mandatory: boolean;
  appliesTo: ProductType[];
  targeting: TargetingState;
  zones: ConfigZone[];
}

interface DiscountForm {
  maxDiscountType: 'percentage' | 'flat';
  maxDiscountValue: string;
  allowCoupons: boolean;
  allowCouponStacking: boolean;
  targeting: TargetingState;
  zones: ConfigZone[];
}


const newTaxEntry = (): TaxForm => ({
  id: Math.random().toString(36).slice(2),
  enabled: true, rate: '0', name: 'VAT', inclusive: false,
  appliesTo: ['physical', 'digital', 'service'],
  zones: [],
});

const newChargeEntry = (): ChargeForm => ({
  name: '', type: 'flat', value: '', enabled: true, mandatory: true,
  appliesTo: [], targeting: emptyTargeting(), zones: [],
});

const TABS: { key: Tab; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { key: 'tax',      label: 'Taxes',    icon: 'receipt-outline' },
  { key: 'charges',  label: 'Charges',  icon: 'cash-outline' },
  { key: 'discount', label: 'Discount', icon: 'pricetag-outline' },
];

// ─── Screen ───────────────────────────────────────────────────────────────────

export const BusinessConfigScreen: React.FC = () => {
  const s = useThemedStyles(getS);
  const zs = useThemedStyles(getZs);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showLeftPane] = useState(Platform.OS === 'web');
  const [showRightPane] = useState(Platform.OS === 'web');
  const [currentTab, setCurrentTab] = useState<Tab>('tax');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const confirmDelete = (title: string, message: string, onConfirm: () => void) =>
    setConfirmModal({ title, message, onConfirm });

  const [taxes, setTaxes] = useState<TaxForm[]>([]);
  const [charges, setCharges] = useState<ChargeForm[]>([]);
  const [addingCharge, setAddingCharge] = useState(false);
  const [newCharge, setNewCharge] = useState<ChargeForm>(newChargeEntry());

  const [discount, setDiscount] = useState<DiscountForm>({
    maxDiscountType: 'percentage', maxDiscountValue: '0',
    allowCoupons: false, allowCouponStacking: false,
    targeting: emptyTargeting(), zones: [],
  });

  // Zone picker state: which rule type + index + zone slot being edited
  type ZonePickerTarget =
    | { rule: 'tax'; ruleId: string; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' }
    | { rule: 'charge'; ruleIndex: number; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' }
    | { rule: 'discount'; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' };
  const [zonePicker, setZonePicker] = useState<ZonePickerTarget | null>(null);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  // Helper: resolve product names for an array of IDs
  const resolveProductNames = async (ids: string[]): Promise<NamedItem[]> =>
    Promise.all(ids.map(async id => {
      try {
        const res = await apiService.getProductById(id);
        return { id, name: res.statusCode === 1000 ? (res.data?.name ?? id) : id };
      } catch { return { id, name: id }; }
    }));

  // Helper: resolve catalogue names for an array of IDs
  const resolveCatalogueNames = async (ids: string[]): Promise<NamedItem[]> => {
    try {
      const res = await apiService.getCatalogues({ limit: 200 });
      if (res.statusCode === 1000) {
        const map = new Map((res.data?.catalogues ?? []).map((c: any) => [c._id, c.name]));
        return ids.map(id => ({ id, name: (map.get(id) as string) ?? id }));
      }
    } catch (_) {}
    return ids.map(id => ({ id, name: id }));
  };

  // Helper: resolve contact names for an array of IDs
  const resolveContactNames = async (ids: string[]): Promise<NamedItem[]> => {
    try {
      const res = await apiService.getUnifiedContacts({ limit: 200, offset: 0 });
      if (res.statusCode === 1000) {
        const all: any[] = res.data?.contacts ?? res.data ?? [];
        const map = new Map(all.map((c: any) => [c._id ?? c.id, c.name ?? c.fullName]));
        return ids.map(id => ({ id, name: (map.get(id) as string) ?? id }));
      }
    } catch (_) {}
    return ids.map(id => ({ id, name: id }));
  };

  const resolveTargeting = async (raw: any): Promise<TargetingState> => ({
    contacts: raw?.contactIds?.length ? await resolveContactNames(raw.contactIds) : [],
    products: raw?.productIds?.length ? await resolveProductNames(raw.productIds) : [],
    catalogues: raw?.catalogueIds?.length ? await resolveCatalogueNames(raw.catalogueIds) : [],
  });


  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBusinessConfig();
      if (res.statusCode === 1000 && res.data) {
        const c = res.data;

        if (Array.isArray(c.taxes)) {
          setTaxes(c.taxes.map((t: any) => ({
            id: t.id || Math.random().toString(36).slice(2),
            enabled: t.enabled ?? false, rate: String(t.rate ?? 0),
            name: t.name ?? 'Tax', inclusive: t.inclusive ?? false,
            appliesTo: t.appliesTo?.length ? t.appliesTo : ['physical', 'digital', 'service'],
            zones: t.zones ?? [],
          })));
        }

        if (Array.isArray(c.charges)) {
          // Load charges with placeholder targeting, then resolve names in background
          const chargeBase: ChargeForm[] = c.charges.map((ch: any) => ({
            name: ch.name, type: ch.type, value: String(ch.value),
            enabled: ch.enabled ?? true,
            mandatory: ch.mandatory ?? true,
            appliesTo: ch.appliesTo ?? [],
            targeting: {
              contacts: (ch.contactIds ?? []).map((id: string) => ({ id, name: id })),
              products: (ch.productIds ?? []).map((id: string) => ({ id, name: id })),
              catalogues: (ch.catalogueIds ?? []).map((id: string) => ({ id, name: id })),
            },
            zones: ch.zones ?? [],
          }));
          setCharges(chargeBase);
          // Resolve names in background
          Promise.all(c.charges.map((ch: any, i: number) => resolveTargeting(ch).then(targeting => ({ i, targeting }))))
            .then(results => setCharges(prev => {
              const next = [...prev];
              results.forEach(({ i, targeting }) => { if (next[i]) next[i] = { ...next[i], targeting }; });
              return next;
            }))
            .catch(() => {});
        }

        if (c.discount) {
          const discountTargeting = await resolveTargeting(c.discount);
          setDiscount({
            maxDiscountType: c.discount.maxDiscountType ?? 'percentage',
            maxDiscountValue: String(c.discount.maxDiscountValue ?? 0),
            allowCoupons: c.discount.allowCoupons ?? false,
            allowCouponStacking: c.discount.allowCouponStacking ?? false,
            targeting: discountTargeting,
            zones: c.discount.zones ?? [],
          });
        }


      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveTaxes = async () => {
    setSaving(true);
    try {
      const payload = taxes.map(t => ({
        id: t.id, enabled: t.enabled,
        rate: parseFloat(t.rate) || 0,
        name: t.name.trim() || 'Tax',
        inclusive: t.inclusive, appliesTo: t.appliesTo,
        zones: t.zones,
      }));
      const res = await apiService.patchBusinessConfigTaxes(payload);
      if (res.statusCode === 1000) showNotification('success', 'Saved', 'Tax settings updated');
      else showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setSaving(false); }
  };

  const saveCharges = async () => {
    setSaving(true);
    try {
      const payload = charges.map(ch => ({
        name: ch.name, type: ch.type,
        value: parseFloat(ch.value) || 0, enabled: ch.enabled,
        mandatory: ch.mandatory,
        appliesTo: ch.appliesTo,
        contactIds: ch.targeting.contacts.map(c => c.id),
        productIds: ch.targeting.products.map(p => p.id),
        catalogueIds: ch.targeting.catalogues.map(c => c.id),
        zones: ch.zones,
      }));
      const res = await apiService.patchBusinessConfigCharges(payload);
      if (res.statusCode === 1000) showNotification('success', 'Saved', 'Charges updated');
      else showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setSaving(false); }
  };

  const saveDiscount = async () => {
    setSaving(true);
    try {
      const res = await apiService.patchBusinessConfigDiscount({
        maxDiscountType: discount.maxDiscountType,
        maxDiscountValue: parseFloat(discount.maxDiscountValue) || 0,
        allowCoupons: discount.allowCoupons,
        allowCouponStacking: discount.allowCouponStacking,
        contactIds: discount.targeting.contacts.map(c => c.id),
        productIds: discount.targeting.products.map(p => p.id),
        catalogueIds: discount.targeting.catalogues.map(c => c.id),
        zones: discount.zones,
      });
      if (res.statusCode === 1000) showNotification('success', 'Saved', 'Discount settings updated');
      else showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setSaving(false); }
  };

  // ─── Zone section (shared across tax / charge / discount) ────────────────────

  const zoneResultToConfigZone = (result: ZoneResult, existing?: ConfigZone): ConfigZone => {
    const geo: Partial<ConfigZone> =
      result.mode === 'city' || result.mode === 'country'
        ? {
            mode: result.mode,
            cities: [result.cityName],
            center: result.center,
            polygon: result.polygon,
            polygons: (result as any).polygons,
          }
        : {
            mode: 'radius',
            center: result.center,
            radiusKm: result.radiusKm,
          };
    return {
      ...geo,
      name: result.name,
      // The rate/amount the agent just typed into the map modal's price
      // field — previously this discarded it entirely for new zones
      // (`existing` is undefined until a zone already exists), so newly
      // added zones always saved with no value at all. `result.price` is
      // always the correct current value: freshly entered for a new zone,
      // or pre-filled from `existing.value` (see ZoneMapPicker's
      // initialData handling) and possibly edited for an existing one.
      value: result.price,
      type: existing?.type,
      enabled: existing?.enabled,
    };
  };

  const handleZoneConfirm = (result: ZoneResult) => {
    if (!zonePicker) return;
    if (zonePicker.rule === 'tax') {
      setTaxes(prev => prev.map(t => {
        if (t.id !== zonePicker.ruleId) return t;
        const zones = [...t.zones];
        const existing = zonePicker.zoneIndex !== null ? zones[zonePicker.zoneIndex] : undefined;
        const zone = zoneResultToConfigZone(result, existing);
        if (zonePicker.zoneIndex !== null) zones[zonePicker.zoneIndex] = zone;
        else zones.push(zone);
        return { ...t, zones };
      }));
    } else if (zonePicker.rule === 'charge') {
      setCharges(prev => prev.map((c, i) => {
        if (i !== zonePicker.ruleIndex) return c;
        const zones = [...c.zones];
        const existing = zonePicker.zoneIndex !== null ? zones[zonePicker.zoneIndex] : undefined;
        const zone = zoneResultToConfigZone(result, existing);
        if (zonePicker.zoneIndex !== null) zones[zonePicker.zoneIndex] = zone;
        else zones.push(zone);
        return { ...c, zones };
      }));
    } else if (zonePicker.rule === 'discount') {
      setDiscount(prev => {
        const zones = [...prev.zones];
        const existing = zonePicker.zoneIndex !== null ? zones[zonePicker.zoneIndex] : undefined;
        const zone = zoneResultToConfigZone(result, existing);
        if (zonePicker.zoneIndex !== null) zones[zonePicker.zoneIndex] = zone;
        else zones.push(zone);
        return { ...prev, zones };
      });
    }
    setZonePicker(null);
  };

  /**
   * Inline zone list + add buttons.
   * valueLabel: what "value" means in context (e.g. "Rate (%)", "Amount", "Cap Value")
   * type: the parent rule type (for the % / flat toggle override)
   */
  const renderConfigZoneSection = (
    zones: ConfigZone[],
    onZonesChange: (z: ConfigZone[]) => void,
    openPicker: (zoneIndex: number | null, mode: 'country' | 'city' | 'radius') => void,
    valueLabel: string,
    parentType?: 'percentage' | 'flat',
  ) => (
    <View style={{ gap: spacing.sm }}>
      <View style={[s.row, { justifyContent: 'space-between' }]}>
        <Text style={s.sectionSubLabel}>Location Pricing</Text>
        <Text style={s.hint}>First matching zone wins</Text>
      </View>

      {zones.map((zone, zi) => (
        <View key={zi} style={zs.zoneCard}>
          <View style={[s.row, { justifyContent: 'space-between' }]}>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={zs.zoneName}>{zone.name}</Text>
              <Text style={zs.zoneMeta}>
                {zone.mode === 'radius'
                  ? `Radius ${zone.radiusKm ?? '?'} km`
                  : zone.cities?.join(', ') ?? zone.mode ?? ''}
              </Text>
            </View>
            <TouchableOpacity onPress={() => openPicker(zi, zone.mode ?? 'city')} style={s.iconBtn} activeOpacity={0.7}>
              <Ionicons name="map-outline" size={16} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => onZonesChange(zones.filter((_, i) => i !== zi))}
              style={s.iconBtn} activeOpacity={0.7}>
              <Ionicons name="trash-outline" size={16} color={colors.error} />
            </TouchableOpacity>
          </View>

          {/* Value override */}
          <View style={s.row}>
            <View style={s.field}>
              <Text style={s.fieldLabel}>{valueLabel} override (blank = use default)</Text>
              <TextInput
                style={[s.input, { width: 110 }]}
                value={zone.value != null ? String(zone.value) : ''}
                onChangeText={v => {
                  const next = [...zones];
                  next[zi] = { ...next[zi], value: v === '' ? undefined : parseFloat(v) || 0 };
                  onZonesChange(next);
                }}
                keyboardType="decimal-pad"
                placeholder="Default"
                placeholderTextColor={colors.textLight}
              />
            </View>
            {/* Type override if parent has flat/percentage */}
            {parentType !== undefined && (
              <View style={[s.field, { flex: 1 }]}>
                <Text style={s.fieldLabel}>Type override</Text>
                <View style={s.row}>
                  {(['flat', 'percentage'] as const).map(tp => (
                    <TouchableOpacity
                      key={tp}
                      style={[s.typeBtn, (zone.type ?? parentType) === tp && s.typeBtnActive]}
                      onPress={() => {
                        const next = [...zones];
                        next[zi] = { ...next[zi], type: tp === parentType ? undefined : tp };
                        onZonesChange(next);
                      }}
                      activeOpacity={0.7}>
                      <Text style={[s.typeBtnText, (zone.type ?? parentType) === tp && s.typeBtnTextActive]}>
                        {tp === 'flat' ? 'Flat' : '%'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
            {/* Disable in zone toggle */}
            <View style={[s.field, { alignItems: 'center' }]}>
              <Text style={s.fieldLabel}>Disable</Text>
              <Switch
                value={zone.enabled === false}
                onValueChange={v => {
                  const next = [...zones];
                  next[zi] = { ...next[zi], enabled: v ? false : undefined };
                  onZonesChange(next);
                }}
                trackColor={{ true: colors.error }}
              />
            </View>
          </View>
        </View>
      ))}

      {/* Add zone buttons */}
      <View style={zs.addZoneRow}>
        {(['country', 'city', 'radius'] as const).map(mode => (
          <TouchableOpacity key={mode} style={zs.addZoneBtn} onPress={() => openPicker(null, mode)} activeOpacity={0.7}>
            <Ionicons name={mode === 'country' ? 'globe-outline' : mode === 'city' ? 'location-outline' : 'radio-button-on-outline'} size={14} color={colors.primary} />
            <Text style={zs.addZoneBtnText}>{mode === 'country' ? 'Country' : mode === 'city' ? 'City' : 'Radius'}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ─── Tax tab ─────────────────────────────────────────────────────────────────

  const updateTax = (id: string, patch: Partial<TaxForm>) =>
    setTaxes(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));

  const toggleTaxAppliesTo = (id: string, type: ProductType) =>
    setTaxes(prev => prev.map(t => t.id === id ? {
      ...t,
      appliesTo: t.appliesTo.includes(type) ? t.appliesTo.filter(x => x !== type) : [...t.appliesTo, type],
    } : t));

  const removeTax = (id: string) =>
    confirmDelete('Remove Tax', 'Remove this tax rate?', () => setTaxes(prev => prev.filter(t => t.id !== id)));

  const renderTaxEntry = (tax: TaxForm) => (
    <View key={tax.id} style={s.card}>
      <View style={s.row}>
        <View style={{ flex: 1 }}>
          <TextInput style={s.input} value={tax.name} onChangeText={v => updateTax(tax.id, { name: v })} placeholder="Tax name (e.g. VAT)" placeholderTextColor={colors.textLight} />
        </View>
        <Switch value={tax.enabled} onValueChange={v => updateTax(tax.id, { enabled: v })} trackColor={{ true: colors.primary }} />
        <TouchableOpacity onPress={() => removeTax(tax.id)} style={s.iconBtn} activeOpacity={0.7}>
          <Ionicons name="trash-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
      <View style={s.row}>
        <View style={s.field}>
          <Text style={s.fieldLabel}>Rate (%)</Text>
          <TextInput style={[s.input, { width: 100 }]} value={tax.rate} onChangeText={v => updateTax(tax.id, { rate: v })} keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
        </View>
        <View style={{ flex: 1, alignItems: 'flex-end', gap: spacing.xs }}>
          <Text style={s.fieldLabel}>Tax Inclusive</Text>
          <Switch value={tax.inclusive} onValueChange={v => updateTax(tax.id, { inclusive: v })} trackColor={{ true: colors.primary }} />
        </View>
      </View>
      <View style={s.field}>
        <Text style={s.fieldLabel}>Applies To</Text>
        <View style={s.chipRow}>
          {PRODUCT_TYPES.map(type => (
            <TouchableOpacity key={type} style={[s.chip, tax.appliesTo.includes(type) && s.chipActive]} onPress={() => toggleTaxAppliesTo(tax.id, type)} activeOpacity={0.7}>
              <Text style={[s.chipText, tax.appliesTo.includes(type) && s.chipTextActive]}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <View style={s.divider} />
      {renderConfigZoneSection(
        tax.zones,
        zones => updateTax(tax.id, { zones }),
        (zoneIndex, mode) => setZonePicker({ rule: 'tax', ruleId: tax.id, zoneIndex, mode }),
        'Rate (%)',
      )}
    </View>
  );

  const renderTaxTab = () => (
    <View style={s.section}>
      {taxes.map(renderTaxEntry)}
      <TouchableOpacity style={s.addBtn} onPress={() => setTaxes(prev => [...prev, newTaxEntry()])} activeOpacity={0.7}>
        <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
        <Text style={s.addBtnText}>Add Tax Rate</Text>
      </TouchableOpacity>
      <TouchableOpacity style={s.saveBtn} onPress={saveTaxes} disabled={saving} activeOpacity={0.8}>
        {saving ? <ActivityIndicator color={colors.white} size="small" /> : <Text style={s.saveBtnText}>Save Tax Settings</Text>}
      </TouchableOpacity>
    </View>
  );

  // ─── Charges tab ─────────────────────────────────────────────────────────────

  const removeCharge = (idx: number) =>
    confirmDelete('Remove Charge', 'Remove this charge?', () => setCharges(prev => prev.filter((_, i) => i !== idx)));

  const addCharge = () => {
    if (!newCharge.name.trim()) { showNotification('warning', 'Required', 'Enter a charge name'); return; }
    setCharges(prev => [...prev, { ...newCharge }]);
    setNewCharge(newChargeEntry());
    setAddingCharge(false);
  };

  const renderChargeCard = (charge: ChargeForm, idx: number) => (
    <View key={idx} style={s.card}>
      <View style={s.row}>
        <TextInput style={[s.input, { flex: 1 }]} value={charge.name}
          onChangeText={v => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, name: v } : c))}
          placeholder="Charge name" placeholderTextColor={colors.textLight} />
        <Switch value={charge.enabled}
          onValueChange={v => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, enabled: v } : c))}
          trackColor={{ true: colors.primary }} />
        <TouchableOpacity onPress={() => removeCharge(idx)} style={s.iconBtn} activeOpacity={0.7}>
          <Ionicons name="trash-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
      <View style={s.row}>
        <View style={{ flex: 1 }}>
          <Text style={s.rowLabel}>Mandatory</Text>
          <Text style={s.hint}>If off, agent can choose whether to apply this charge per order</Text>
        </View>
        <Switch value={charge.mandatory}
          onValueChange={v => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, mandatory: v } : c))}
          trackColor={{ true: colors.primary }} />
      </View>
      <View style={s.row}>
        {(['flat', 'percentage'] as const).map(type => (
          <TouchableOpacity key={type} style={[s.typeBtn, charge.type === type && s.typeBtnActive]}
            onPress={() => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, type } : c))} activeOpacity={0.7}>
            <Text style={[s.typeBtnText, charge.type === type && s.typeBtnTextActive]}>{type === 'flat' ? 'Flat' : '%'}</Text>
          </TouchableOpacity>
        ))}
        <TextInput style={[s.input, { flex: 1 }]} value={charge.value}
          onChangeText={v => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, value: v } : c))}
          keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
      </View>
      <View style={s.field}>
        <Text style={s.fieldLabel}>Product Types (empty = all)</Text>
        <View style={s.chipRow}>
          {PRODUCT_TYPES.map(type => (
            <TouchableOpacity key={type}
              style={[s.chip, charge.appliesTo.includes(type) && s.chipActive]}
              onPress={() => setCharges(prev => prev.map((c, i) => i === idx ? {
                ...c, appliesTo: c.appliesTo.includes(type) ? c.appliesTo.filter(x => x !== type) : [...c.appliesTo, type],
              } : c))}
              activeOpacity={0.7}>
              <Text style={[s.chipText, charge.appliesTo.includes(type) && s.chipTextActive]}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <View style={s.divider} />
      <TargetingSection
        label="Targeting (empty = applies to all)"
        value={charge.targeting}
        onChange={targeting => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, targeting } : c))}
      />
      <View style={s.divider} />
      {renderConfigZoneSection(
        charge.zones,
        zones => setCharges(prev => prev.map((c, i) => i === idx ? { ...c, zones } : c)),
        (zoneIndex, mode) => setZonePicker({ rule: 'charge', ruleIndex: idx, zoneIndex, mode }),
        charge.type === 'percentage' ? 'Rate (%)' : 'Amount',
        charge.type,
      )}
    </View>
  );

  const renderChargesTab = () => (
    <View style={s.section}>
      {charges.map(renderChargeCard)}
      {addingCharge ? (
        <View style={s.card}>
          <Text style={s.sectionSubLabel}>New Charge</Text>
          <TextInput style={s.input} value={newCharge.name}
            onChangeText={v => setNewCharge(p => ({ ...p, name: v }))}
            placeholder="Charge name (e.g. Service Fee)" placeholderTextColor={colors.textLight} autoFocus />
          <View style={s.row}>
            {(['flat', 'percentage'] as const).map(type => (
              <TouchableOpacity key={type} style={[s.typeBtn, newCharge.type === type && s.typeBtnActive]}
                onPress={() => setNewCharge(p => ({ ...p, type }))} activeOpacity={0.7}>
                <Text style={[s.typeBtnText, newCharge.type === type && s.typeBtnTextActive]}>{type === 'flat' ? 'Flat' : '%'}</Text>
              </TouchableOpacity>
            ))}
            <TextInput style={[s.input, { flex: 1 }]} value={newCharge.value}
              onChangeText={v => setNewCharge(p => ({ ...p, value: v }))}
              keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
          </View>
          <View style={s.field}>
            <Text style={s.fieldLabel}>Product Types (empty = all)</Text>
            <View style={s.chipRow}>
              {PRODUCT_TYPES.map(type => (
                <TouchableOpacity key={type}
                  style={[s.chip, newCharge.appliesTo.includes(type) && s.chipActive]}
                  onPress={() => setNewCharge(p => ({ ...p, appliesTo: p.appliesTo.includes(type) ? p.appliesTo.filter(x => x !== type) : [...p.appliesTo, type] }))}
                  activeOpacity={0.7}>
                  <Text style={[s.chipText, newCharge.appliesTo.includes(type) && s.chipTextActive]}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={s.row}>
            <View style={{ flex: 1 }}>
              <Text style={s.rowLabel}>Mandatory</Text>
              <Text style={s.hint}>If off, agent can choose whether to apply this charge per order</Text>
            </View>
            <Switch value={newCharge.mandatory}
              onValueChange={v => setNewCharge(p => ({ ...p, mandatory: v }))}
              trackColor={{ true: colors.primary }} />
          </View>
          <View style={s.divider} />
          <TargetingSection
            label="Targeting (empty = applies to all)"
            value={newCharge.targeting}
            onChange={targeting => setNewCharge(p => ({ ...p, targeting }))}
          />
          <View style={s.row}>
            <TouchableOpacity style={[s.outlineBtn, { flex: 1 }]} onPress={() => { setAddingCharge(false); setNewCharge(newChargeEntry()); }} activeOpacity={0.7}>
              <Text style={s.outlineBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.saveBtn, { flex: 1 }]} onPress={addCharge} activeOpacity={0.8}>
              <Text style={s.saveBtnText}>Add</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <TouchableOpacity style={s.addBtn} onPress={() => setAddingCharge(true)} activeOpacity={0.7}>
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={s.addBtnText}>Add Extra Charge</Text>
        </TouchableOpacity>
      )}
      {charges.length > 0 && (
        <TouchableOpacity style={s.saveBtn} onPress={saveCharges} disabled={saving} activeOpacity={0.8}>
          {saving ? <ActivityIndicator color={colors.white} size="small" /> : <Text style={s.saveBtnText}>Save Charges</Text>}
        </TouchableOpacity>
      )}
    </View>
  );

  // ─── Discount tab ─────────────────────────────────────────────────────────────

  const renderDiscountTab = () => (
    <View style={s.section}>
      <View style={s.card}>
        <View style={s.field}>
          <Text style={s.fieldLabel}>Max Discount Cap Type</Text>
          <Text style={s.hint}>Cap is enforced on targeted items (or full order if no targeting)</Text>
          <View style={[s.chipRow, { marginTop: spacing.xs }]}>
            {(['percentage', 'flat'] as const).map(type => (
              <TouchableOpacity key={type}
                style={[s.typeBtn, discount.maxDiscountType === type && s.typeBtnActive]}
                onPress={() => setDiscount(p => ({ ...p, maxDiscountType: type }))} activeOpacity={0.7}>
                <Text style={[s.typeBtnText, discount.maxDiscountType === type && s.typeBtnTextActive]}>
                  {type === 'percentage' ? 'Percentage (%)' : 'Fixed Amount'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <View style={s.field}>
          <Text style={s.fieldLabel}>Cap Value ({discount.maxDiscountType === 'percentage' ? '%, 0 = no cap' : 'amount, 0 = no cap'})</Text>
          <TextInput style={s.input} value={discount.maxDiscountValue}
            onChangeText={v => setDiscount(p => ({ ...p, maxDiscountValue: v }))}
            keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
        </View>
        <View style={s.divider} />
        <View style={s.row}>
          <View style={{ flex: 1 }}>
            <Text style={s.rowLabel}>Allow Coupon Codes</Text>
            <Text style={s.hint}>Customers can apply codes at checkout</Text>
          </View>
          <Switch value={discount.allowCoupons} onValueChange={v => setDiscount(p => ({ ...p, allowCoupons: v }))} trackColor={{ true: colors.primary }} />
        </View>
        {discount.allowCoupons && (
          <>
            <View style={s.divider} />
            <View style={s.row}>
              <View style={{ flex: 1 }}>
                <Text style={s.rowLabel}>Allow Coupon Stacking</Text>
                <Text style={s.hint}>Allow multiple coupons on one order</Text>
              </View>
              <Switch value={discount.allowCouponStacking} onValueChange={v => setDiscount(p => ({ ...p, allowCouponStacking: v }))} trackColor={{ true: colors.primary }} />
            </View>
          </>
        )}
        <View style={s.divider} />
        <TargetingSection
          label="Cap Targeting (empty = apply cap on full order total)"
          value={discount.targeting}
          onChange={targeting => setDiscount(p => ({ ...p, targeting }))}
        />
        <View style={s.divider} />
        {renderConfigZoneSection(
          discount.zones,
          zones => setDiscount(p => ({ ...p, zones })),
          (zoneIndex, mode) => setZonePicker({ rule: 'discount', zoneIndex, mode }),
          discount.maxDiscountType === 'percentage' ? 'Cap (%)' : 'Cap Amount',
          discount.maxDiscountType,
        )}
      </View>
      {discount.allowCoupons && (
        <TouchableOpacity style={s.linkCard} onPress={() => navigateTo('coupons')} activeOpacity={0.7}>
          <View style={s.linkCardIcon}><Ionicons name="pricetag-outline" size={20} color={colors.primary} /></View>
          <Text style={s.linkCardText}>Manage Coupons</Text>
          <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
        </TouchableOpacity>
      )}
      <TouchableOpacity style={s.saveBtn} onPress={saveDiscount} disabled={saving} activeOpacity={0.8}>
        {saving ? <ActivityIndicator color={colors.white} size="small" /> : <Text style={s.saveBtnText}>Save Discount Settings</Text>}
      </TouchableOpacity>
    </View>
  );

  // ─── Layout ───────────────────────────────────────────────────────────────────

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane} onLeftPaneClose={() => {}} leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={showRightPane} onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Taxes & Charges" onBack={() => navigateTo('settings')} />}
    >
      {loading ? (
        <View style={s.center}><ActivityIndicator size="large" color={colors.primary} /></View>
      ) : (
        <>
          <View style={s.tabBar}>
            {TABS.map(tab => (
              <TouchableOpacity key={tab.key}
                style={[s.tabItem, currentTab === tab.key && s.tabItemActive]}
                onPress={() => setCurrentTab(tab.key)} activeOpacity={0.7}>
                <Ionicons name={tab.icon} size={18} color={currentTab === tab.key ? colors.primary : colors.textLight} />
                <Text style={[s.tabLabel, currentTab === tab.key && s.tabLabelActive]}>{tab.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
            {currentTab === 'tax'      && renderTaxTab()}
            {currentTab === 'charges'  && renderChargesTab()}
            {currentTab === 'discount' && renderDiscountTab()}
          </ScrollView>
        </>
      )}
      <ConfirmModal
        visible={!!confirmModal}
        title={confirmModal?.title ?? ''}
        message={confirmModal?.message ?? ''}
        onConfirm={() => { confirmModal?.onConfirm(); setConfirmModal(null); }}
        onCancel={() => setConfirmModal(null)}
      />

      <ZoneMapPicker
        visible={zonePicker !== null}
        onClose={() => setZonePicker(null)}
        initialMode={zonePicker?.mode}
        initialData={(() => {
          if (!zonePicker || zonePicker.zoneIndex === null) return undefined;
          let zone: ConfigZone | undefined;
          if (zonePicker.rule === 'tax') zone = taxes.find(t => t.id === zonePicker.ruleId)?.zones[zonePicker.zoneIndex!];
          else if (zonePicker.rule === 'charge') zone = charges[zonePicker.ruleIndex]?.zones[zonePicker.zoneIndex!];
          else if (zonePicker.rule === 'discount') zone = discount.zones[zonePicker.zoneIndex!];
          if (!zone) return undefined;
          if (zone.mode === 'radius') return { mode: 'radius' as const, name: zone.name, price: zone.value ?? 0, center: zone.center, radiusKm: zone.radiusKm };
          return { mode: (zone.mode ?? 'city') as 'city' | 'country', name: zone.name, price: zone.value ?? 0, cityName: zone.cities?.[0] ?? '', center: zone.center, polygon: zone.polygon, polygons: zone.polygons } as any;
        })()}
        onConfirm={handleZoneConfirm}
      />
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getS = () => StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  tabBar: { flexDirection: 'row', backgroundColor: colors.background.surface, borderBottomWidth: 1, borderBottomColor: colors.border },
  tabItem: { flex: 1, alignItems: 'center', paddingVertical: spacing.sm, gap: 2 },
  tabItemActive: { borderBottomWidth: 2, borderBottomColor: colors.primary },
  tabLabel: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  tabLabelActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  scroll: { flex: 1 },
  scrollContent: { padding: spacing.md, gap: spacing.md, paddingBottom: 40 },
  section: { gap: spacing.md },
  card: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.md, gap: spacing.md, ...designTokens.shadows.sm },
  divider: { height: 1, backgroundColor: colors.border },
  field: { gap: spacing.xs },
  fieldLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textLight },
  sectionSubLabel: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: -spacing.xs },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.regular, backgroundColor: colors.background.surface },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  rowLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textDark, flex: 1 },
  iconBtn: { padding: spacing.xs },
  chipRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 20, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipActive: { borderColor: colors.primary, backgroundColor: colors.primary + '15' },
  chipText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  chipTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  saveBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: spacing.sm, alignItems: 'center', justifyContent: 'center', ...designTokens.shadows.sm },
  saveBtnText: { fontSize: typography.fontSize.sm, color: colors.white, fontFamily: typography.fontFamily.semibold },
  outlineBtn: { borderRadius: 10, borderWidth: 1, borderColor: colors.border, paddingVertical: spacing.sm, alignItems: 'center' },
  outlineBtnText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  addBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, paddingVertical: spacing.sm, borderWidth: 1, borderColor: colors.primary, borderRadius: 10, borderStyle: 'dashed' },
  addBtnText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
  typeBtn: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 6, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  typeBtnActive: { borderColor: colors.primary, backgroundColor: colors.primary + '15' },
  typeBtnText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  typeBtnTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  linkCard: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.md, ...designTokens.shadows.sm },
  linkCardIcon: { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.primary + '15', justifyContent: 'center', alignItems: 'center' },
  linkCardText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  pickerBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, alignSelf: 'flex-start', marginTop: spacing.xs },
  pickerBtnText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});

const getZs = () => StyleSheet.create({
  zoneCard: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: spacing.sm, gap: spacing.sm, backgroundColor: colors.background.primary },
  zoneName: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  zoneMeta: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  addZoneRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  addZoneBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderColor: colors.primary, borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: 4 },
  addZoneBtnText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});
